import { PALETTE } from './constants';
import { clamp } from './format';
import type { WheelNode } from './types';

/** Iepriekš aprēķināts jomu koka indekss. Virsjomas vērtība = apakšjomu vidējais (rekursīvi). */
export interface Index {
  node: Map<string, WheelNode>;
  kids: Map<string | null, string[]>;
  val: Map<string, number>;
  /** Pati joma un visi tās senči (no jomas uz augšu). */
  anc: Map<string, string[]>;
  top: Map<string, string>;
  depth: Map<string, number>;
  leaves: Map<string, string[]>;
  tops: string[];
  color: (id: string) => string;
}

export function buildIndex(nodes: WheelNode[]): Index {
  const node = new Map<string, WheelNode>();
  const kids = new Map<string | null, string[]>();
  const order = new Map<string, number>();
  nodes.forEach((n, i) => {
    node.set(n.id, n);
    kids.set(n.id, []);
    order.set(n.id, i);
  });
  kids.set(null, []);
  // Efektīvais vecāks: neeksistējošs vecāks vai cikls → joma kļūst par virsjomu.
  const parentOf = new Map<string, string | null>();
  nodes.forEach((n) => {
    let p = n.parentId && node.has(n.parentId) ? n.parentId : null;
    const seen = new Set([n.id]);
    let cur = p;
    while (cur) {
      if (seen.has(cur)) {
        p = null;
        break;
      }
      seen.add(cur);
      const nx: string | null = node.get(cur)!.parentId;
      cur = nx && node.has(nx) ? nx : null;
    }
    parentOf.set(n.id, p);
  });
  nodes.forEach((n) => kids.get(parentOf.get(n.id)!)!.push(n.id));
  // Stabila kārtošana pēc sortOrder, tad pēc ievietošanas secības.
  kids.forEach((list) =>
    list.sort((a, b) => node.get(a)!.sortOrder - node.get(b)!.sortOrder || order.get(a)! - order.get(b)!)
  );

  const val = new Map<string, number>();
  const visiting = new Set<string>();
  const calc = (id: string): number => {
    const known = val.get(id);
    if (known !== undefined) return known;
    if (visiting.has(id)) return 0; // aizsardzība pret cikliem
    visiting.add(id);
    const k = kids.get(id)!;
    const v = k.length ? k.reduce((s, c) => s + calc(c), 0) / k.length : clamp(Number(node.get(id)!.value) || 0, 0, 10);
    visiting.delete(id);
    val.set(id, v);
    return v;
  };
  nodes.forEach((n) => calc(n.id));

  const anc = new Map<string, string[]>();
  nodes.forEach((n) => {
    const a: string[] = [];
    let p: string | null = n.id;
    let guard = 0;
    while (p && node.has(p) && guard++ < 64) {
      a.push(p);
      p = parentOf.get(p) ?? null;
    }
    anc.set(n.id, a);
  });
  const top = new Map<string, string>();
  const depth = new Map<string, number>();
  nodes.forEach((n) => {
    const a = anc.get(n.id)!;
    top.set(n.id, a[a.length - 1]);
    depth.set(n.id, a.length - 1);
  });
  const leaves = new Map<string, string[]>();
  const lf = (id: string): string[] => {
    const known = leaves.get(id);
    if (known) return known;
    const k = kids.get(id)!;
    const r = k.length ? k.flatMap(lf) : [id];
    leaves.set(id, r);
    return r;
  };
  nodes.forEach((n) => lf(n.id));
  const tops = kids.get(null)!;
  const topColor = new Map<string, string>();
  tops.forEach((t, i) => topColor.set(t, node.get(t)!.color || PALETTE[i % PALETTE.length]));
  return {
    node, kids, val, anc, top, depth, leaves, tops,
    color: (id: string) => topColor.get(top.get(id) || '') || '#888888'
  };
}

export const pathName = (idx: Index, id: string) =>
  (idx.anc.get(id) || []).slice().reverse().map((x) => idx.node.get(x)!.name).join(' › ');

/** Vai `maybeDesc` ir `id` pēcnācējs (vai tā pati joma). */
export const isInSubtree = (idx: Index, id: string, maybeDesc: string) => (idx.anc.get(maybeDesc) || []).includes(id);

/** Jomas koka kārtībā (priekš izvēlnēm). */
export function treeOrder(idx: Index, exclude?: Set<string>): string[] {
  const out: string[] = [];
  const walk = (id: string) => {
    if (exclude && exclude.has(id)) return;
    out.push(id);
    idx.kids.get(id)!.forEach(walk);
  };
  idx.tops.forEach(walk);
  return out;
}

/** Nākamā vēl neizmantotā virsjomas krāsa. */
export function nextColor(idx: Index): string {
  const used = new Set(idx.tops.map((t) => idx.color(t)));
  return PALETTE.find((c) => !used.has(c)) || PALETTE[idx.tops.length % PALETTE.length];
}
